import React, { useCallback, useMemo } from "react";
/**
 * This helps you to dispatch a custom event
 * Use useEventSubscriber to listen to these events
 *
 * @example
 * ```
 *  eventDispatcher(props.instanceId, "my-custom-event", {message: "this is a custom event"})
 * ```
 *
 *  @export
 */
const eventDispatcher = (instanceId, eventName, data) => {
    if (!data)
        data = {};
    data.instanceId = instanceId;
    document.dispatchEvent(new CustomEvent(eventName, { detail: data }));
};
const eventListener = (event, instanceId, eventName, handler) => {
    let data = event.detail;
    if (data && data.instanceId && data.instanceId == instanceId) {
        // this is a event dispatched from same instance 
        // ignore these to avoid infinite re-renders 
        console.log("Ignoring the event. Event dispatched from same instance");
        return;
    }
    return handler(event.detail, eventName);
};
/**
 * This helps you to subscribe to a custom event dispatched through the eventDispatcher
 *
 * @example
 * ```
 *  useEventSubscriber(props.instanceId, "my-custom-event", (data?: any) => {
 *      console.log(data?.message)
 *  })
 * ```
 *
 * @export
 */
const useEventSubscriber = (instanceId, eventName, callback) => {
    const handler = React.useRef(null);
    React.useEffect(() => {
        handler.current = callback;
    }, [callback]);
    React.useEffect(() => {
        const listener = (e) => eventListener(e, instanceId, eventName, handler.current);
        document.addEventListener(eventName, listener);
        return () => {
            document.removeEventListener(eventName, listener);
        };
    }, [instanceId, eventName]);
};
/**
 * @export
 * event handler for multiple events
 * either can pass a simple configuration with array of event names and a shared callback
 * of a pull config with different callback for each event.
 */
const useMultiEventSubscriber = (instanceId, config) => {
    const handlersRef = React.useRef(new Map());
    const processEventHandlers = useCallback((_config) => {
        handlersRef.current.clear();
        if (_config.sharedCallback) {
            // Shared callback mode
            _config.events.forEach((eventName) => {
                handlersRef.current.set(eventName, _config.sharedCallback);
            });
        }
        else {
            // Individual callbacks mode
            _config.events.forEach(({ eventName, callback }) => {
                handlersRef.current.set(eventName, callback);
            });
        }
    }, []);
    const eventNames = useMemo(() => {
        processEventHandlers(config);
        return (config === null || config === void 0 ? void 0 : config.sharedCallback)
            ? config.events
            : config.events.map(({ eventName }) => (eventName));
    }, [config, processEventHandlers]);
    // Subscribe to events
    React.useEffect(() => {
        const listeners = new Map();
        eventNames.forEach((eventName) => {
            const handler = handlersRef.current.get(eventName);
            const listener = (e) => eventListener(e, instanceId, eventName, handler);
            document.addEventListener(eventName, listener);
            listeners.set(eventName, listener);
        });
        return () => {
            listeners.forEach((listener, eventName) => {
                document.removeEventListener(eventName, listener);
            });
        };
    }, [instanceId, eventNames]);
};
export { useEventSubscriber, eventDispatcher, useMultiEventSubscriber };
